import { SELL_API as axios } from '@services/axiosInstance';
import type { ShoppingCartDTO, CartItemDTO, CharacteristicDTO } from '@t/api.types';

const cartPath = (sessionId: string) => `/sessions/${sessionId}/shopping-cart`;

export const createShoppingCart = async (sessionId: string): Promise<ShoppingCartDTO> => {
  const { data } = await axios.post<ShoppingCartDTO>(cartPath(sessionId));
  return data;
};

export const getShoppingCart = async (sessionId: string): Promise<ShoppingCartDTO> => {
  const { data } = await axios.get<ShoppingCartDTO>(cartPath(sessionId));
  return data;
};

export const ensureShoppingCart = (sessionId: string): Promise<ShoppingCartDTO> =>
  axios
    .get<ShoppingCartDTO>(cartPath(sessionId), { validateStatus: (status) => status === 200 || status === 404 })
    .then((res) => (res.status === 404 ? createShoppingCart(sessionId) : res.data));

export const addTopOffer = async (sessionId: string, item: CartItemDTO): Promise<ShoppingCartDTO> => {
  const { data } = await axios.post<ShoppingCartDTO>(`${cartPath(sessionId)}/top-offers`, item);
  return data;
};

export const addEvItem = async (sessionId: string, item: CartItemDTO): Promise<ShoppingCartDTO> => {
  const { data } = await axios.post<ShoppingCartDTO>(`${cartPath(sessionId)}/ev-items`, item);
  return data;
};

export const deleteTopOffer = async (sessionId: string, itemId: string): Promise<ShoppingCartDTO> => {
  const { data } = await axios.delete<ShoppingCartDTO>(`${cartPath(sessionId)}/top-offers/${itemId}`);
  return data;
};

export const addChildItem = async (
  sessionId: string,
  parentOfferingCode: string,
  items: CartItemDTO[]
): Promise<ShoppingCartDTO> => {
  const { data } = await axios.post<ShoppingCartDTO>(
    `${cartPath(sessionId)}/top-offers/${parentOfferingCode}/children`,
    items
  );
  return data;
};

export const deleteChildItem = async (sessionId: string, childItemId: string): Promise<ShoppingCartDTO> => {
  const { data } = await axios.delete<ShoppingCartDTO>(`${cartPath(sessionId)}/items/${childItemId}`);
  return data;
};

export const deleteChildItems = async (
  sessionId: string,
  childItemIds: string[]
): Promise<ShoppingCartDTO> => {
  const { data } = await axios.delete<ShoppingCartDTO>(`${cartPath(sessionId)}/items`, {
    data: childItemIds,
  });
  return data;
};

export const upsertCharacteristics = async (
  sessionId: string,
  itemId: string,
  characteristics: CharacteristicDTO[]
): Promise<ShoppingCartDTO> => {
  const { data } = await axios.put<ShoppingCartDTO>(
    `${cartPath(sessionId)}/items/${itemId}/characteristics`,
    characteristics
  );
  return data;
};

export const submitCart = async (sessionId: string): Promise<ShoppingCartDTO> => {
  const { data } = await axios.post<ShoppingCartDTO>(`${cartPath(sessionId)}/submit`);
  return data;
};

export const abandonCart = async (cartId: string): Promise<void> => {
  await axios.post(`/shopping-carts/${cartId}/abandon`);
};
